import {
    computed, ref, Ref
} from 'vue';
import { sortTimelineData } from '../utils/timelineDataTransformers';
import {
    UserData, RosterShift,
    ShiftTaskItem
} from '../utils/types';

interface FilteredData {
    data: UserData[];
    itemsCount: number;
}

interface UseTimelineDataOptions {
    rowData: Ref<UserData[]>;
    totalTeamSchedule?: Ref<number>;
    currentPage: Ref<number>;
    pageSize: Ref<number>;
    groupBy: Ref<string>;
}

function updateMinDate(currentMin: Date | null, newDate: Date): Date | null {
    if (Number.isNaN(newDate.getTime())) return currentMin;
    if (!currentMin || newDate < currentMin) return newDate;
    return currentMin;
}

function updateMaxDate(currentMax: Date | null, newDate: Date): Date | null {
    if (Number.isNaN(newDate.getTime())) return currentMax;
    if (!currentMax || newDate > currentMax) return newDate;
    return currentMax;
}

function processTask(task: ShiftTaskItem, minDate: Date | null, maxDate: Date | null): {
  minDate: Date | null;
  maxDate: Date | null;
} {
    const taskStart = new Date(task.startTime ?? '');
    const taskEnd = new Date(task.endTime ?? '');

    return {
        minDate: updateMinDate(minDate, taskStart),
        maxDate: updateMaxDate(maxDate, taskEnd)
    };
}

function getMinMaxFromRowData(rows: UserData[]): {
  minDate: Date | null;
  maxDate: Date | null;
} {
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    rows.forEach((userData) => {
        userData?.shifts?.forEach((shift) => {
            shift?.shiftTasks?.forEach((task) => {
                const result = processTask(task, minDate, maxDate);
                minDate = result.minDate;
                maxDate = result.maxDate;
            });
        });
    });

    return { minDate, maxDate };
}

export function useTimelineData({
    rowData,
    currentPage,
    pageSize,
    groupBy
}: UseTimelineDataOptions) {
    const isFiltered = ref(false);
    const filterStartDate = ref<string | null>(null);
    const filterEndDate = ref<string | null>(null);

    const filteredApiData = computed((): FilteredData | null => {
        if (!rowData.value || rowData.value.length === 0) return null;
        
        // Sort the data first
        const sortedData = sortTimelineData(rowData.value, groupBy.value);
        
        if (!isFiltered.value) {
            return { data: sortedData, itemsCount: sortedData.length };
        }

        const startDate = new Date(filterStartDate.value!);
        const endDate = new Date(filterEndDate.value!);
        endDate.setHours(23, 59, 59, 999); // Include entire end date

        const filteredData = sortedData.map((userData) => {
            const filteredShifts = userData.shifts.filter((shift: RosterShift) => {
                const shiftStart = new Date(shift.startTime);
                const shiftEnd = new Date(shift.endTime);
                // Include shift if it overlaps with the filter range
                return shiftStart <= endDate && shiftEnd >= startDate;
            }).map((shift: RosterShift) => {
                // Filter tasks within the shift that fall in the date range
                const filteredTasks = shift.shiftTasks?.filter((task: ShiftTaskItem) => {
                    const taskStart = new Date(task.startTime ?? '');
                    const taskEnd = new Date(task.endTime ?? '');
                    return taskStart <= endDate && taskEnd >= startDate;
                });
                return { ...shift, shiftTasks: filteredTasks };
            }).filter((shift: RosterShift) => (shift.shiftTasks?.length ?? 0) > 0); // Only include shifts with tasks

            return { ...userData, shifts: filteredShifts };
        }).filter((userData) => userData.shifts.length > 0); // Only include users with shifts

        return {
            data: filteredData,
            itemsCount: filteredData.length
        };
    });

    const totalUsers = computed(() => {
        return filteredApiData.value?.data?.length || 0;
    });

    // Total distinct teams (for team-based pagination when grouping by TEAMS)
    const totalTeams = computed(() => {
        if (!filteredApiData.value?.data) return 0;
        // Prefer teamId for uniqueness; fallback to teamName if missing
        const teamSet = new Set<number | string>();
        filteredApiData.value.data.forEach((userData) => {
            const teamId = userData.user?.teamId;
            const teamName = userData.user?.teamName || 'Blanks';
            if (teamId !== undefined && teamId !== null) {
                teamSet.add(teamId);
            } else {
                teamSet.add(teamName);
            }
        });
        return teamSet.size;
    });

    const paginatedData = computed((): FilteredData | null => {
        if (!filteredApiData.value || !filteredApiData.value.data) return null;

        const start = (currentPage.value - 1) * pageSize.value;
        const end = start + pageSize.value;
        const paginatedUsers = filteredApiData.value.data.slice(start, end);

        return {
            data: paginatedUsers,
            itemsCount: paginatedUsers.length
        };
    });

    const initializeDateRange = () => {
        if (!rowData.value || rowData.value.length === 0) return;

        const { minDate, maxDate } = getMinMaxFromRowData(rowData.value);

        if (minDate && maxDate ) {
            filterStartDate.value = minDate.toISOString().split('T')[0];
            filterEndDate.value = maxDate.toISOString().split('T')[0];
        }
    };

    const applyDateFilter = () => {
        if (filterStartDate.value && filterEndDate.value) {
            isFiltered.value = true;
        }
    };

    const resetDateFilter = () => {
        initializeDateRange();
        isFiltered.value = false;
    };

    return {
        isFiltered,
        filterStartDate,
        filterEndDate,
        filteredApiData,
        totalUsers,
        totalTeams,
        paginatedData,
        initializeDateRange,
        applyDateFilter,
        resetDateFilter
    };
}
